import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc, 
  writeBatch, 
  query, 
  limit 
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * إعادة تعيين قاعدة البيانات بالكامل
 * تحذير: هذه العملية ستحذف جميع البيانات في قاعدة البيانات
 * @returns وعد بنتيجة عملية إعادة التعيين
 */
export const resetDatabase = async (): Promise<boolean> => {
  try {
    console.log('بدء عملية إعادة تعيين قاعدة البيانات...');
    
    // قائمة المجموعات التي سيتم حذفها
    const collections = [
      'products',
      'categories',
      'orders',
      'customers',
      'inventory',
      'users',
      'settings',
      'notifications',
      'coupons',
      'shipping'
    ];
    
    // حذف كل مجموعة
    for (const collectionName of collections) {
      await deleteCollection(collectionName);
    }
    
    console.log('تم إعادة تعيين قاعدة البيانات بنجاح');
    return true;
  } catch (error) {
    console.error('Error resetting database:', error);
    throw error;
  }
};

/**
 * حذف مجموعة بالكامل
 * @param collectionName اسم المجموعة
 * @returns وعد بنتيجة عملية الحذف
 */
const deleteCollection = async (collectionName: string): Promise<void> => {
  try {
    console.log(`حذف مجموعة ${collectionName}...`);
    
    // الحصول على جميع المستندات في المجموعة
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);
    
    // إذا كانت المجموعة فارغة، لا داعي للمتابعة
    if (snapshot.empty) {
      console.log(`مجموعة ${collectionName} فارغة، لا يوجد شيء للحذف`);
      return;
    }
    
    // حذف المستندات على دفعات
    const batchSize = 500; // الحد الأقصى لعدد العمليات في الدفعة الواحدة
    const totalDocs = snapshot.size;
    let deletedCount = 0;
    
    // استخدام الدفعات لتحسين الأداء
    for (let i = 0; i < totalDocs; i += batchSize) {
      const batch = writeBatch(db);
      const docsToDelete = snapshot.docs.slice(i, i + batchSize);
      
      docsToDelete.forEach(docSnapshot => {
        batch.delete(doc(db, collectionName, docSnapshot.id));
      });
      
      await batch.commit();
      deletedCount += docsToDelete.length;
      console.log(`تم حذف ${deletedCount} من ${totalDocs} مستند في مجموعة ${collectionName}`);
    }
    
    console.log(`تم حذف مجموعة ${collectionName} بنجاح`);
  } catch (error) {
    console.error(`Error deleting collection ${collectionName}:`, error);
    throw error;
  }
};

/**
 * حذف مستند محدد
 * @param collectionName اسم المجموعة
 * @param documentId معرف المستند
 * @returns وعد بنتيجة عملية الحذف
 */
export const deleteDocument = async (collectionName: string, documentId: string): Promise<boolean> => {
  try {
    console.log(`حذف مستند ${documentId} من مجموعة ${collectionName}...`);
    
    // حذف المستند
    await deleteDoc(doc(db, collectionName, documentId));
    
    console.log(`تم حذف المستند ${documentId} بنجاح`);
    return true;
  } catch (error) {
    console.error(`Error deleting document ${documentId}:`, error);
    return false;
  }
};

/**
 * الحصول على عدد المستندات في مجموعة
 * @param collectionName اسم المجموعة
 * @returns وعد بعدد المستندات
 */
export const getCollectionCount = async (collectionName: string): Promise<number> => {
  try {
    // الحصول على جميع المستندات في المجموعة
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);
    
    return snapshot.size;
  } catch (error) {
    console.error(`Error getting collection count for ${collectionName}:`, error);
    return 0;
  }
};

/**
 * التحقق من وجود مستند
 * @param collectionName اسم المجموعة
 * @param documentId معرف المستند
 * @returns وعد بنتيجة التحقق
 */
export const documentExists = async (collectionName: string, documentId: string): Promise<boolean> => {
  try {
    // الحصول على المستند
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDocs(query(collection(db, collectionName), limit(1)));
    
    return !docSnap.empty;
  } catch (error) {
    console.error(`Error checking if document ${documentId} exists:`, error);
    return false;
  }
};

export default {
  resetDatabase,
  deleteCollection,
  deleteDocument,
  getCollectionCount,
  documentExists
};
